import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { ColumnDef } from '@app/shared/types/ui';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-column-selector',
  standalone: true,
  imports: [
    DragDropModule,
    MatCheckboxModule,
    MatIconModule,
    MatDividerModule,
    MatMenuModule
],
  template: `
    <div
      cdkDropList
      [cdkDropListData]="columns()"
      (cdkDropListDropped)="onColumnDrop($event)"
      class="column-selector__list"
    >
      @for (col of columns(); track col.key) {
        <div cdkDrag class="column-selector__item">
          <mat-checkbox
            color="primary"
            [checked]="col.visible"
            [disabled]="col.always"
            (change)="onColumnToggle(col)"
            (click)="$event.stopPropagation()"
            (keydown.space)="$event.stopPropagation()"
          >
            {{ col.label }}
          </mat-checkbox>
          <span class="column-selector__drag-handle" cdkDragHandle>
            <mat-icon>drag_indicator</mat-icon>
          </span>
        </div>
      }
    </div>

    <mat-divider />

    <button
      mat-menu-item
      class="column-selector__reset-button"
      (click)="onResetColumns()"
      (keydown.space)="$event.stopPropagation()">
      <mat-icon>refresh</mat-icon>
      <span>Restablecer columnas</span>
    </button>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 280px;
      }

      .column-selector__list {
        max-height: 350px;
        overflow-y: auto;
        padding: clamp(0.25rem, 1vh, 0.5rem) 0;
      }

      .column-selector__item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: clamp(0.25rem, 1.5vh, 0.25rem) 0.5rem;
        cursor: move;
        &.cdk-drag-preview {
          background: #fff;
          border-radius: 4px;
          box-shadow: 0 5px 5px -3px rgba(0, 0, 0, 0.2),
                      0 8px 10px 1px rgba(0, 0, 0, 0.14),
                      0 3px 14px 2px rgba(0, 0, 0, 0.12);
        }

        &.cdk-drag-placeholder {
          opacity: 0.2;
          background: #ccc;
        }
      }

      .column-selector__drag-handle {
        color: #666;
        cursor: grab;

        &:active {
          cursor: grabbing;
        }
      }

      .column-selector__reset-button {
        span {
          margin-left: 8px;
        }
      }

      .cdk-drag-animating {
        transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
      }

      .column-selector__list.cdk-drop-list-dragging .column-selector__item:not(.cdk-drag-placeholder) {
        transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnSelectorComponent {
  columns = input.required<ColumnDef[]>();
  columnToggle = output<ColumnDef>();
  columnDrop = output<CdkDragDrop<ColumnDef[]>>();
  resetColumns = output<void>();

  onColumnToggle(column: ColumnDef): void {
    this.columnToggle.emit(column);
  }

  onColumnDrop(event: CdkDragDrop<ColumnDef[]>): void {
    this.columnDrop.emit(event);
  }

  onResetColumns(): void {
    this.resetColumns.emit();
  }
}
